import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { useEmployerRoute } from "../../../../../hooks";
import { Group } from "../../../../../@types/group.types";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../../utils/commonFunctions/date";

const chipMessage = "This report will get generated for all the list of terminated employees of the selected group.";

const EmployeeTerminationReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		start_date: "",
		end_date: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	// const handleValidation = useCallback(async () => {
	// 	const { group_number, start_date, end_date } = reportFieldDetails;

	// 	_validation.current = {
	// 		...validation,
	// 		reportFieldDetails: {},
	// 		status: "invalid"
	// 	};
	// 	let flag = true;

	// 	// employee validation checking
	// 	if (group_number === null) {
	// 		_validation.current.reportFieldDetails["group_number"] = "Group is required";
	// 		_validation.current["status"] = "invalid";

	// 		flag = false;
	// 	}
	// 	if (start_date.length === 0) {
	// 		_validation.current.reportFieldDetails["start_date"] = "Start date is required";
	// 		_validation.current["status"] = "invalid";
	// 		flag = false;
	// 	}
	// 	if (start_date.length !== 0) {
	// 		if (compareInvalidDates(start_date, end_date)) {
	// 			_validation.current.reportFieldDetails["start_date"] = "Start Date cannot be greater than End Date!";
	// 			_validation.current["status"] = "invalid";
	// 			flag = false;
	// 		}
	// 	}
	// 	if (end_date.length !== 0) {
	// 		if (compareInvalidDates(start_date, end_date)) {
	// 			_validation.current.reportFieldDetails["end_date"] = "End Date cannot be less than Start Date!";
	// 			_validation.current["status"] = "invalid";
	// 			flag = false;
	// 		}
	// 	}

	// 	if (flag === true) _validation.current["status"] = "valid";
	// 	else {
	// 		_validation.current["status"] = "invalid";
	// 		alert("Please, Select a Group to generate the report!");
	// 	}
	// 	setValidation(Object.assign({}, _validation.current));
	// 	return _validation.current["status"];
	// }, [reportFieldDetails, validation]);

	const handleValidation = useCallback(async () => {
		const { group_number, start_date, end_date } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};

		let flag = true;

		// Group number validation
		if (!group_number) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
			alert("Please, Select a Group to generate the report!");
		}

		// Convert date strings to Date objects for comparison
		const startDateObj = new Date(start_date);
		const endDateObj = new Date(end_date);

		// Start date and end date validation (optional)
		if (start_date && end_date) {
			if (startDateObj > endDateObj) {
				_validation.current.reportFieldDetails["start_date"] = "Start Date cannot be greater than End Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
			if (endDateObj < startDateObj) {
				_validation.current.reportFieldDetails["end_date"] = "End Date cannot be less than Start Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag) {
			_validation.current["status"] = "valid";
		} else {
			_validation.current["status"] = "invalid";
		}

		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[reportFieldDetails]
	);

	const handleEnableCheckbox = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, checked } = event.target;
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: checked }));
		},
		[reportFieldDetails]
	);

	const handleDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
		},
		[reportFieldDetails]
	);
	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);
	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Employee Termination Report Generation");
					const { group_number, start_date, end_date } = reportFieldDetails;
					// Set default dates if not provided
					// const currentDate = new Date();
					// const defaultDateString = `${currentDate.getFullYear()}-${
					// 	currentDate.getMonth() + 1
					// }-${currentDate.getDate()}`;

					const startDate = start_date ? new Date(start_date) : new Date();
					const endDate = end_date ? new Date(end_date) : new Date();

					const payload = {
						group_number,
						start_date:
							start_date !== ""
								? startDate.getFullYear() + "-" + (startDate.getMonth() + 1) + "-" + startDate.getDate()
								: null,
						end_date:
							end_date !== ""
								? endDate.getFullYear() + "-" + (endDate.getMonth() + 1) + "-" + endDate.getDate()
								: null
					};

					const response: Member = await trackPromise(api.reports.employeeTermination(payload));

					if (response) {
						// console.log("Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Employee Termination Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message: "Employee Termination Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("Employee Termination Report Generation Unsuccessful!");
				throw error;
			}
		},

		[handleValidation, reportFieldDetails, snackbarAPICallProps, user]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employee_termination_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Employee Termination Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						},
						{
							label: "Start Date",
							name: "start_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter start date",
							value: reportFieldDetails.start_date,
							type: "date"
						},
						{
							label: "End Date",
							name: "end_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter end date",
							value: reportFieldDetails.end_date,
							type: "date"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleEnableCheckbox,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default EmployeeTerminationReport;
